import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { BGVGallery } from '@/features/gallery/types'

// Batch fetch a best-effort cover image per gallery
// Priority: gallery.cover_image -> image with is_cover=true -> latest image
export function useGalleryCovers(galleries: Array<Pick<BGVGallery, 'id' | 'cover_image'>>) {
  const idsNeedingCover = useMemo(() => (
    (galleries || [])
      .filter(g => !g.cover_image)
      .map(g => g.id)
      .sort((a, b) => a - b)
  ), [galleries])

  const { data, isLoading, error } = useQuery({
    queryKey: ['gallery-covers', idsNeedingCover],
    enabled: idsNeedingCover.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gallery_all_images')
        .select('gallery_id,image_url,is_cover,created_at')
        .in('gallery_id', idsNeedingCover)
        .order('is_cover', { ascending: false })
        .order('created_at', { ascending: false })
      if (error) throw error
      const map = new Map<number, string>()
      for (const row of data || []) {
        const gid = (row as any).gallery_id as number
        const url = (row as any).image_url as string
        if (!map.has(gid) && url) {
          map.set(gid, url)
        }
      }
      return map
    }
  })

  const coverById = useMemo(() => {
    const out: Record<number, string | undefined> = {}
    for (const g of galleries || []) {
      out[g.id] = g.cover_image || data?.get(g.id) || undefined
    }
    return out
  }, [galleries, data])

  return { coverById, isLoading, error }
}

export default useGalleryCovers
